<script module lang="ts">
  import { environments } from '../../models/environment.type';
  import { items } from '../../models/items';
  import { TraitRegistry, type TraitType } from '../../models/trait-registry';

  /** Enemies reference environments by id; the section they link to filters on title. */
  const environmentTitles = new Map<string, string>(
    environments.map((environment) => [environment.id as string, environment.title]),
  );

  /** An Items value is the item's own id, and the Items section filters on title. */
  const itemTitles = new Map<string, string>(
    items.map((item) => [item.type as string, item.title]),
  );

  /**
   * A trait carries its title and text as statics on the registered class, so
   * they can be read without constructing one -- a Trait wants an owner and runs
   * init() on construction, neither of which makes sense for a reference entry.
   */
  function describeTrait(id: TraitType) {
    const TraitClass = TraitRegistry.get(id) as
      { baseTitle?: string; baseText?: string } | undefined;

    return {
      id,
      title: TraitClass?.baseTitle ?? id,
      text: TraitClass?.baseText ?? '',
    };
  }
</script>

<script lang="ts">
  import Portrait from '../../components/portrait.svelte';
  import { createDie } from '../../models/die.type';
  import { glossary } from '../../../store';
  import Die from '../../dice-tray/die.svelte';
  import Text from '../../components/text.svelte';
  import type { EnemyDefinition } from '../../models/enemy.type';
  import type { Die as DieObject } from '../../models/die.type';

  interface Props {
    // The Enemies section passes EnemyDefinition rows, not live Player instances.
    // `tier` is stamped on in glossary.svelte, which is the only place that still
    // knows whether a row came from enemies, bosses or special.
    data: EnemyDefinition & { tier?: string };
  }

  let { data }: Props = $props();

  /**
   * Most enemies roll the same die twice -- 43 of the 47 are three matched pairs
   * -- so the list is collapsed to one entry per type with a count. Four are not
   * pairs at all (one rolls six different dice, three roll four pairs), which is
   * why this counts rather than assuming.
   */
  let diceGroups: Array<{ die: DieObject; count: number }> = [];

  if (data?.dice) {
    data.dice.forEach((type, index) => {
      const existing = diceGroups.find((group) => group.die.type === type);
      if (existing) {
        existing.count++;
        return;
      }
      // Unique per enemy and die type. The old ids were 'p' + index, which
      // repeated across every enemy in the list.
      diceGroups.push({
        die: createDie(type, index, `${data.type}-${type}`),
        count: 1,
      });
    });
  }

  let traits = $derived((data.traits ?? []).map(describeTrait));

  let places = $derived(
    (data.environments ?? []).map((id) => ({
      id: id as string,
      title: environmentTitles.get(id as string) ?? (id as string),
    })),
  );

  let carried = $derived(
    (data.inventory ?? []).map((type) => ({
      type: type as string,
      title: itemTitles.get(type as string) ?? (type as string),
    })),
  );

  /**
   * Opens Techniques filtered to what this enemy can build. Passing one die per
   * type rather than all six is equivalent: technique.svelte unions the faces of
   * everything it is given, and a duplicate die contributes no new faces.
   */
  function openTechniques() {
    glossary.selected.set('techniques');
    glossary.searchTerm.set('');
    glossary.searchDie.set(diceGroups.map((group) => group.die));
  }

  function openEnvironment(title: string) {
    glossary.selected.set('environments');
    glossary.searchTerm.set(title);
  }

  function openItem(title: string) {
    glossary.selected.set('items');
    glossary.searchTerm.set(title);
  }
</script>

<div class="container entry {data.tier ?? ''}">
  <div class="portrait">
    <Portrait data={{ type: data.type }}></Portrait>
  </div>

  <div class="information">
    <div class="heading">
      <h2>{data.title}</h2>

      {#if data.tier && data.tier !== 'enemy'}
        <span class="tier {data.tier}">{data.tier}</span>
      {/if}
    </div>

    {#if data.text}
      <Text data={data.text} class="blurb" />
    {/if}

    <div class="loadout">
      {#each diceGroups as group (group.die.id)}
        <div class="die-container">
          <Die die={group.die} die3D={false} hover={true} sizeMod={1} />
          {#if group.count > 1}
            <span class="count">&times;{group.count}</span>
          {/if}
        </div>
      {/each}

      {#if diceGroups.length}
        <button class="techniques" onclick={openTechniques}>View Techniques</button>
      {/if}
    </div>

    {#if traits.length}
      <div class="traits">
        {#each traits as trait (trait.id)}
          <div class="trait">
            <span class="trait-title">{trait.title}</span>
            {#if trait.text}
              <span class="trait-text">{trait.text}</span>
            {/if}
          </div>
        {/each}
      </div>
    {/if}

    {#if data.strengths?.length || data.weaknesses?.length}
      <div class="tactics">
        {#if data.strengths?.length}
          <div class="column strengths">
            <span class="column-label">Strengths</span>
            <ul>
              {#each data.strengths as strength}
                <li>{strength}</li>
              {/each}
            </ul>
          </div>
        {/if}

        {#if data.weaknesses?.length}
          <div class="column weaknesses">
            <span class="column-label">Weaknesses</span>
            <ul>
              {#each data.weaknesses as weakness}
                <li>{weakness}</li>
              {/each}
            </ul>
          </div>
        {/if}
      </div>
    {/if}

    {#if carried.length}
      <div class="places">
        <span class="places-label">Carries</span>
        {#each carried as item (item.type)}
          <button class="place" onclick={() => openItem(item.title)}>
            {item.title}
          </button>
        {/each}
      </div>
    {/if}

    {#if places.length}
      <div class="places">
        <span class="places-label">Found in</span>
        {#each places as place (place.id)}
          <button class="place" onclick={() => openEnvironment(place.title)}>
            {place.title}
          </button>
        {/each}
      </div>
    {/if}
  </div>
</div>

<style lang="less">
  @import '../../../defines.less';

  .container {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    grid-gap: 16px;
    align-items: start;

    text-align: left;

    // The list concatenates enemies, bosses and special into one alphabetical
    // run, so the frame around the portrait is what separates a boss from a
    // wolf -- see --portrait-accent in components/portrait.svelte. There is no
    // card edge to carry it any more; the section is one document.
    &.boss {
      --portrait-accent: var(--purple);
    }
    &.special {
      --portrait-accent: var(--cyan);
    }

    @media @mobile {
      grid-template-columns: minmax(0, 1fr);
      justify-items: center;
      text-align: center;
    }
  }

  .portrait {
    // Portrait sizes itself off --column-size, which is 300px app-wide for the
    // battle screen.
    --column-size: 150px;

    @media @mobile {
      --column-size: 130px;
    }
  }

  .information {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    grid-gap: 8px;
    width: 100%;

    @media @mobile {
      align-items: center;
    }
  }

  .heading {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    grid-gap: 10px;

    h2 {
      font-size: 1.4rem;
      text-transform: capitalize;
    }
  }

  .tier {
    padding: 2px 10px;
    font-size: 0.65rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    border-radius: 999px;
    border: 1px solid currentColor;
    opacity: 0.85;

    &.boss {
      color: var(--purple);
    }
    &.special {
      color: var(--cyan);
    }
  }

  .container :global(.blurb) {
    font-size: 0.9rem;
    opacity: 0.85;
  }

  .loadout {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    grid-gap: 12px;
    margin-top: 2px;

    @media @mobile {
      justify-content: center;
    }

    .die-container {
      position: relative;
    }

    // How many of that die the enemy actually rolls.
    .count {
      position: absolute;
      right: -4px;
      bottom: -4px;
      padding: 0 5px;
      font-size: 0.7rem;
      line-height: 1.5;
      background: var(--background-colour-dark);
      border: 1px solid var(--background-colour-light);
      border-radius: 999px;
      pointer-events: none;
    }
  }

  .techniques {
    font-size: 0.8rem;
    padding: 8px 16px;
    border: 1px solid var(--background-colour-light);
    box-shadow: none;
    background: var(--background-colour-dark);
    transition:
      border-color 0.2s,
      color 0.2s;

    &::after {
      content: '\00a0\203A';
      display: inline-block;
      transition: transform 0.2s;
    }

    &:hover,
    &:focus-visible {
      filter: none;
      color: @edge-shine;
      border-color: @edge-warm;

      &::after {
        transform: translateX(3px);
      }
    }

    &:active {
      box-shadow: none;
    }
  }

  .traits {
    display: flex;
    flex-direction: column;
    grid-gap: 4px;
    width: 100%;

    // A line of the entry rather than a filled strip across it -- the strip
    // was a card in all but name, and the only one left on the page.
    .trait {
      padding: 2px 0 2px 12px;
      font-size: 0.85rem;
      border-left: 2px solid var(--background-colour-light);
      text-align: left;
    }

    .trait-title {
      font-weight: 700;

      &::after {
        content: ' \2014\00a0';
        opacity: 0.5;
      }
    }

    .trait-text {
      opacity: 0.8;
    }
  }

  .tactics {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    grid-gap: 10px;
    width: 100%;
    text-align: left;

    .column-label {
      display: block;
      margin-bottom: 2px;
      font-size: 0.65rem;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      opacity: 0.6;
    }

    .strengths .column-label {
      color: var(--green);
    }
    .weaknesses .column-label {
      color: var(--red);
    }

    ul {
      margin: 0;
      padding-left: 18px;
      font-size: 0.85rem;
      opacity: 0.85;
    }
  }

  .places {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    grid-gap: 6px;

    @media @mobile {
      justify-content: center;
    }

    .places-label {
      font-size: 0.65rem;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      opacity: 0.55;
    }

    .place {
      // Reset of the global button.less pill.
      padding: 2px 10px;
      font-size: 0.7rem;
      background: var(--background-colour-dark);
      border: 1px solid var(--background-colour-light);
      border-radius: 999px;
      box-shadow: none;
      opacity: 0.75;
      transition:
        opacity 0.2s,
        border-color 0.2s,
        color 0.2s;

      &:hover,
      &:focus-visible {
        filter: none;
        opacity: 1;
        color: @edge-shine;
        border-color: @edge-warm;
      }

      &:active {
        box-shadow: none;
      }
    }
  }
</style>
